import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static values = {
        blockId: String,
        url: String
    }

    // ===========================================
    // GENERAL STYLE UPDATES
    // ===========================================

    updatePadding(event) {
        const value = event.target.value
        const direction = event.target.dataset.direction

        let target = this.getContentArea()

        // For buttons (Anchor tags), padding should apply to the wrapper (parent)
        // to control spacing around the button, not inside it.
        if (target && target.tagName === 'A' && target.parentElement) {
            target = target.parentElement
        }

        if (target) {
            target.style[direction === 'top' ? 'paddingTop' : 'paddingBottom'] = `${value}px`
        }

        this.saveField(`padding_${direction}`, value)
    }

    updateFontSize(event) {
        const value = event.target.value
        this.applyStyle('fontSize', `${value}px`)
        this.saveField('font_size', value)
    }

    updateAlignment(event) {
        const value = event.target.value
        this.applyStyle('textAlign', value)

        // Also apply to parent wrapper if in tight mode (often parent handles alignment)
        const content = this.getContentArea()
        if (content && content.parentElement) {
            content.parentElement.style.textAlign = value
        }
        this.saveField('alignment', value)
    }

    updateLineHeight(event) {
        const value = event.target.value
        this.applyStyle('lineHeight', value)
        this.saveField('line_height', value)
    }

    updateColor(event) {
        const value = event.target.value
        this.applyStyle('color', value)
        this.saveField('color', value)
    }

    updateBackgroundColor(event) {
        const value = event.target.value
        this.applyStyle('backgroundColor', value)
        this.saveField('background_color', value)
    }

    updateFontFamily(event) {
        const value = event.target.value

        if (value) {
            this._loadFont(value)
        }

        // Fallback stack
        const stack = `'${value}', 'Cairo', 'Tajawal', system-ui, sans-serif`
        this.applyStyle('fontFamily', stack)
        this.saveField('font_family', value)
    }

    _loadFont(fontName) {
        if (!fontName) return;
        const cleanName = fontName.trim();
        const id = `font-${cleanName.replace(/\s+/g, '-').toLowerCase()}`;

        if (document.getElementById(id)) return;

        const link = document.createElement('link');
        link.id = id;
        link.rel = 'stylesheet';
        link.href = `https://fonts.googleapis.com/css2?family=${cleanName.replace(/\s+/g, '+')}:wght@400;700&display=swap`;

        document.head.appendChild(link);
        console.log(`🔤 Settings Loaded font: ${cleanName}`);
    }

    updateToggle(event) {
        const prop = event.target.dataset.styleProp // bold, italic, underline
        const isChecked = event.target.checked

        if (prop === 'bold') {
            this.applyStyle('fontWeight', isChecked ? '700' : '400')
            this.saveField('bold', isChecked)
        } else if (prop === 'italic') {
            this.applyStyle('fontStyle', isChecked ? 'italic' : 'normal')
            this.saveField('italic', isChecked)
        } else if (prop === 'underline') {
            // Handle potential conflict with other decorations? adhering to simple model first.
            this.applyStyle('textDecoration', isChecked ? 'underline' : 'none')
            this.saveField('underline', isChecked)
        }
    }

    updateBackgroundMode(event) {
        const value = event.target.value
        const content = this.getContentArea()
        if (content) {
            if (value === 'tight') {
                content.style.display = 'inline-block'
                content.style.width = 'auto'
                if (content.parentElement) {
                    const currentAlign = content.style.textAlign || getComputedStyle(content).textAlign
                    content.parentElement.style.textAlign = currentAlign
                }
            } else {
                content.style.display = 'block'
                content.style.width = '100%'
            }
        }
        this.saveField('background_mode', value)
    }

    // ===========================================
    // TEXT CONTENT (heading / paragraph textarea)
    // ===========================================

    updateText(event) {
        const value = event.target.value
        const content = this.getContentArea()
        if (content) {
            content.textContent = value
        }
        this.saveField('text', value)
    }

    // ===========================================
    // BUTTON UPDATES
    // ===========================================

    updateButtonText(event) {
        const value = event.target.value
        const content = this.getContentArea()
        if (content) {
            // Text is often the first text node, or we can look for specific span if we wrap it
            // Simple approach: Replace text content but keep icon? 
            // Better: Wrap text in a span in preview. For now, let's assume text node.
            // Actually, we can just find the text node.
            const textNode = Array.from(content.childNodes).find(node => node.nodeType === Node.TEXT_NODE && node.textContent.trim().length > 0)
            if (textNode) {
                textNode.textContent = value
            } else {
                // Prepend if not found (icon might be there)
                content.prepend(document.createTextNode(value))
            }
        }
        this.saveField('text', value)
    }

    updateButtonUrl(event) {
        const value = event.target.value
        const content = this.getContentArea()
        if (content && content.tagName === 'A') {
            content.href = value
        }
        this.saveField('url', value)
    }

    updateButtonSize(event) {
        this.updateButtonClass(event, 'size')
    }

    updateButtonRadius(event) {
        this.updateButtonClass(event, 'radius')
    }

    updateButtonShadow(event) {
        this.updateButtonClass(event, 'shadow')
    }

    updateButtonIcon(event) {
        const value = event.target.value
        const content = this.getContentArea()
        if (content) {
            const iconMap = {
                'arrow': '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>',
                'chevron': '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>',
                'check': '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>',
                'plus': '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>',
                'none': ''
            }

            // 1. Try to find existing .btn-icon
            let iconContainer = content.querySelector('.btn-icon')

            // 2. If not found, look for any span containing an SVG (legacy support)
            if (!iconContainer) {
                const existingSvg = content.querySelector('svg')
                if (existingSvg && existingSvg.parentElement && existingSvg.parentElement.tagName === 'SPAN') {
                    iconContainer = existingSvg.parentElement
                    iconContainer.classList.add('btn-icon', 'ml-2')
                }
            }

            // 3. If still not found, create it
            if (!iconContainer) {
                iconContainer = document.createElement('span')
                iconContainer.className = 'btn-icon ml-2'
                content.appendChild(iconContainer)
            }

            if (value === 'none') {
                iconContainer.innerHTML = ''
                iconContainer.classList.add('hidden')
            } else {
                iconContainer.classList.remove('hidden')
                iconContainer.innerHTML = `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">${iconMap[value] || ''}</svg>`
            }
        }
        this.saveField('icon', value)
    }

    updateButtonClass(event, type) {
        const value = event.target.value
        const content = this.getContentArea()
        if (!content) return

        // Mappings
        const maps = {
            size: {
                'small': ['px-3', 'py-1.5', 'text-sm'],
                'medium': ['px-5', 'py-2.5', 'text-base'],
                'large': ['px-8', 'py-4', 'text-lg']
            },
            radius: {
                'none': ['rounded-none'],
                'sm': ['rounded'],
                'md': ['rounded-md'],
                'lg': ['rounded-lg'],
                'xl': ['rounded-xl'],
                '2xl': ['rounded-2xl'],
                'full': ['rounded-full']
            },
            shadow: {
                'none': ['shadow-none'],
                'sm': ['shadow-sm'],
                'md': ['shadow'],
                'lg': ['shadow-lg'],
                'xl': ['shadow-xl']
            }
        }

        const currentMap = maps[type]
        if (!currentMap) return

        // Remove all possible classes for this type
        Object.values(currentMap).flat().forEach(c => content.classList.remove(c))

        // Add new classes
        // Default to medium/lg/md if not found found
        const defaults = { size: 'medium', radius: 'lg', shadow: 'md' }
        const classes = currentMap[value] || currentMap[defaults[type]]
        classes.forEach(c => content.classList.add(c))

        this.saveField(type, value)
    }

    // ===========================================
    // COLUMN UPDATES
    // ===========================================

    connect() {
        this.activeColumnIndex = 0
        this.updateColumnTabs()
    }

    switchToColumn(event) {
        this.activeColumnIndex = parseInt(event.currentTarget.dataset.index)
        this.updateColumnTabs()
    }

    setColumnCount(event) {
        const count = event.currentTarget.dataset.count
        const form = this.element.querySelector('form')
        const input = form.querySelector('input[name="page_block[column_count]"]')

        if (input && count) {
            input.value = count
            // We use requestSubmit() to trigger standard form submission and Turbo
            form.requestSubmit()
        }
    }

    updateColumnTabs() {
        // Find all tabs and sections within this controller's scope
        const tabs = this.element.querySelectorAll('[data-column-index]')
        const sections = this.element.querySelectorAll('[data-column-section]')

        // Clamp index if out of bounds (e.g. after reduction)
        if (this.activeColumnIndex >= tabs.length && tabs.length > 0) {
            this.activeColumnIndex = Math.max(0, tabs.length - 1)
        }

        tabs.forEach((tab, i) => {
            if (i === this.activeColumnIndex) {
                tab.classList.add('bg-white', 'text-indigo-600', 'shadow-sm')
                tab.classList.remove('text-slate-500', 'hover:text-slate-900')
            } else {
                tab.classList.remove('bg-white', 'text-indigo-600', 'shadow-sm')
                tab.classList.add('text-slate-500', 'hover:text-slate-900')
            }
        })

        sections.forEach((section, i) => {
            if (i === this.activeColumnIndex) {
                section.classList.remove('hidden')
            } else {
                section.classList.add('hidden')
            }
        })
    }

    updateColumnStyle(event) {
        const input = event.target
        const value = input.value
        // Extract index from ID "col_bg_text_0" or name
        // "page_block[content][column_styles][0][background_color]"
        const match = input.name.match(/\[column_styles\]\[(\d+)\]/)
        const index = match ? parseInt(match[1]) : 0

        this.applyColumnStyle(index, 'backgroundColor', value)

        // Save using dot notation for nested update
        // "column_styles.0.background_color"
        this.saveField(`column_styles.${index}.background_color`, value)
    }

    syncColumnBgFromPicker(event) {
        const value = event.target.value
        // col_bg_picker_0 -> col_bg_text_0
        const textId = event.target.id.replace('picker', 'text')
        const previewId = event.target.id.replace('picker', 'preview')

        const textInput = document.getElementById(textId)
        if (textInput) textInput.value = value

        const preview = document.getElementById(previewId)
        if (preview) preview.style.backgroundColor = value

        // Trigger manual update
        // We simulate an event or direct call
        // We need the simulated event target to have the correct name for parsing
        if (textInput) {
            this.updateColumnStyle({ target: textInput })
        }
    }

    applyColumnStyle(index, property, value) {
        const blockEl = document.getElementById(`block-${this.blockIdValue}`)
        if (!blockEl) return

        // Find all columns
        // They have class "admin-row-column-{id}"
        const columns = blockEl.querySelectorAll(`.admin-row-column-${this.blockIdValue}`)
        if (columns[index]) {
            columns[index].style[property] = value
        }
    }


    // ===========================================
    // ROW UPDATES
    // ===========================================

    updateRowGap(event) {
        const value = event.target.value
        this.applyCssVariable('--row-gap', `${value}px`)
        this.saveField('gap', `${value}px`)
    }

    updateRowPadding(event) {
        const value = event.target.value
        this.applyCssVariable('--row-padding', `${value}px`)
        this.saveField('padding', `${value}px`)
    }

    updateRowMinHeight(event) {
        const value = event.target.value
        // Handle "auto" or pixel values
        const finalValue = value === 'auto' ? 'auto' : `${value}px`
        this.applyCssVariable('--row-min-height', finalValue)
        this.saveField('min_height', finalValue)
    }

    updateRowRatio(event) {
        const value = event.target.value
        // Use more robust dataset access, fallback to 2
        const count = parseInt(event.target.dataset.columns || event.target.getAttribute('data-columns')) || 2

        let template = "1fr 1fr" // Default

        if (count === 2) {
            // MATCHING RAILS PAGE HELPER LOGIC EXACTLY
            if (value === '50-50') template = "1fr 1fr"
            else if (value === '60-40') template = "3fr 2fr"
            else if (value === '40-60') template = "2fr 3fr"
            else if (value === '70-30') template = "2fr 1fr" // Matched to Rails: 2fr 1fr
            else if (value === '30-70') template = "1fr 2fr" // Matched to Rails: 1fr 2fr
            else if (value === '80-20') template = "4fr 1fr"
            else if (value === '20-80') template = "1fr 4fr"
        } else if (count === 3) {
            if (value === '33-33-33') template = "1fr 1fr 1fr"
            else if (value === '50-25-25') template = "2fr 1fr 1fr"
            else if (value === '25-50-25') template = "1fr 2fr 1fr"
            else if (value === '25-25-50') template = "1fr 1fr 2fr"
            else if (value === '40-30-30') template = "4fr 3fr 3fr"
        }

        console.log(`[BlockSettings] updateRowRatio: ${value} (count=${count}) -> ${template}`)
        this.applyCssVariable('--row-col-template', template)
        this.saveField('column_ratio', value)
    }

    updateRowVerticalAlignment(event) {
        const value = event.target.value
        const map = {
            'top': 'items-start',
            'center': 'items-center',
            'bottom': 'items-end'
        }

        const blockEl = document.getElementById(`block-${this.blockIdValue}`)
        if (!blockEl) return

        const grid = blockEl.querySelector(`[class*="row-grid-"]`)
        if (grid) {
            grid.classList.remove('items-start', 'items-center', 'items-end')
            grid.classList.add(map[value] || 'items-start')
        }

        this.saveField('vertical_alignment', value)
    }

    updateMobileReverse(event) {
        const isChecked = event.target.checked
        const blockEl = document.getElementById(`block-${this.blockIdValue}`)
        if (!blockEl) return

        // Targeted Row Grid
        const grid = blockEl.querySelector(`[class*="row-grid-"]`)
        if (grid) {
            if (isChecked) {
                grid.classList.add('flex', 'flex-col-reverse', 'md:grid')
            } else {
                grid.classList.remove('flex', 'flex-col-reverse', 'md:grid')
            }
        }
        this.saveField('mobile_reverse', isChecked)
    }

    applyCssVariable(name, value) {
        const content = this.getContentArea() // Should find the main row wrapper
        if (content) {
            content.style.setProperty(name, value)
        }
    }

    // ===========================================
    // COLOR PICKER SYNC
    // ===========================================

    syncColorFromPicker(event) {
        const value = event.target.value
        // Assumes ID convention: something_picker -> something_text
        const textId = event.target.id.replace('picker', 'text')
        const textInput = document.getElementById(textId)
        if (textInput) textInput.value = value

        this.updateColor({ target: { value } })
    }

    syncBgFromPicker(event) {
        const value = event.target.value
        const textId = event.target.id.replace('picker', 'text')
        const textInput = document.getElementById(textId)
        if (textInput) textInput.value = value

        this.updateBackgroundColor({ target: { value } })
    }

    applyPreset(event) {
        const { color, type } = event.currentTarget.dataset
        // type is 'color' or 'background'
        // Find inputs in this form controller scope
        // But color buttons might be outside? No they are inside the form.

        // We look for inputs ending with proper suffix
        const suffix = type === 'color' ? 'color_text' : 'bg_text'
        const pickerSuffix = type === 'color' ? 'color_picker' : 'bg_picker'

        const textInput = this.element.querySelector(`[id$="${suffix}"]`)
        const pickerInput = this.element.querySelector(`[id$="${pickerSuffix}"]`)

        if (textInput) textInput.value = color
        if (pickerInput && color !== 'transparent') pickerInput.value = color

        if (type === 'color') {
            this.updateColor({ target: { value: color } })
        } else {
            this.updateBackgroundColor({ target: { value: color } })
        }
    }

    // ===========================================
    // ACTIONS
    // ===========================================

    close() {
        // If opened via turbo frame or separate window, history.back() is usually the way to "cancel/done" back to index
        window.history.back()
    }

    // ===========================================
    // HELPERS
    // ===========================================

    applyStyle(property, value) {
        const content = this.getContentArea()
        if (content) {
            content.style[property] = value
        }
    }

    getContentArea() {
        const blockElement = document.getElementById(`block-${this.blockIdValue}`)
        if (!blockElement) return null
        return blockElement.querySelector('.content-area') || blockElement.querySelector('[contenteditable="true"]') || blockElement
    }

    async saveField(field, value) {
        // Debounce
        if (this.saveTimeout) clearTimeout(this.saveTimeout)
        this.saveTimeout = setTimeout(async () => {
            let url = this.urlValue
            if (!url.endsWith('/inline_update')) {
                url = `${url}/inline_update`
            }

            try {
                const response = await fetch(url, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
                    },
                    body: JSON.stringify({ field, value })
                })

                if (response.ok) {
                    const data = await response.json()
                    // Update preview with sanitized value if provided
                    if (data.sanitized_value && data.sanitized_value !== value) {
                        // Special handling for URL to ensure protocol is visible
                        if (field === 'url') {
                            const content = this.getContentArea()
                            if (content && content.tagName === 'A') {
                                content.href = data.sanitized_value
                            }
                            // Optional: Update input if it doesn't match? 
                            // Only if not focused prevents jumps, but difficult to track focus here easily.
                            // For now, syncing the preview is the most important "Live" feedback.
                        }
                    }
                }
            } catch (error) {
                console.error('Error saving:', error)
            }
        }, 500)
    }

    stopProp(event) {
        event.stopPropagation()
    }
}
